import { Navigate, Route } from 'react-router-dom';
import { MANAGED_SERVICES, routes as vivaRoutes } from './lib/viva';
import { apiDocsPlugin, ApiExplorerPage } from '@backstage/plugin-api-docs';
import {
  CatalogEntityPage,
  CatalogIndexPage,
  catalogPlugin,
} from '@backstage/plugin-catalog';
import {
  CatalogImportPage,
  catalogImportPlugin,
} from '@backstage/plugin-catalog-import';
import { ScaffolderPage, scaffolderPlugin } from '@backstage/plugin-scaffolder';
import { orgPlugin } from '@backstage/plugin-org';
import { SearchPage } from '@backstage/plugin-search';
import {
  TechDocsIndexPage,
  techdocsPlugin,
  TechDocsReaderPage,
} from '@backstage/plugin-techdocs';
import { TechDocsAddons } from '@backstage/plugin-techdocs-react';
import { ReportIssue } from '@backstage/plugin-techdocs-module-addons-contrib';
import { UserSettingsPage } from '@backstage/plugin-user-settings';
import { apis } from './apis';
import { entityPage } from './components/catalog/EntityPage';
import { searchPage } from './components/search/SearchPage';
import { Root } from './components/Root';
import { VivaTemplateCard } from './components/scaffolder';
import { ResourceEditPage } from './components/resourceEdit';
import { AiAccessPage, OAuthConsentPage } from './components/aiAccess';
import { isEditTemplate, isHiddenTemplate } from './lib/resourceEdit';
import { HomePage } from './components/home';
import { ClustersPage } from './components/catalog/ClustersPage';
import { ModelsPage } from './components/catalog/ModelsPage';
import { DatabasesPage } from './components/catalog/DatabasesPage';
import { ServicesPage } from './components/catalog/ServicesPage';
import { JupyterHubPage } from './components/jupyterhub';
import { ProxmoxPage } from './components/proxmox/ProxmoxPage';
import { FeastPage } from './components/feast';
import { AirflowPage } from './components/airflow';
import { DefectDojoPage } from './components/defectdojo';
import { RustfsPage } from './components/rustfs';
import { BrokersPage } from './components/catalog/BrokersPage';
import {
  MongodbPage,
  RabbitmqPage,
  ValkeyPage,
} from './components/catalog/OperatorDatastorePages';
import { ManagedServicePage } from './components/catalog/ManagedDatastoresPage';
import { KubernetesIcon, MlModelIcon } from './components/icons';
import CloudIcon from '@material-ui/icons/Cloud';
import LockIcon from '@material-ui/icons/Lock';
import { VisitListener } from '@backstage/plugin-home';
import { UnifiedThemeProvider } from '@backstage/theme';
import LightIcon from '@material-ui/icons/WbSunny';
import DarkIcon from '@material-ui/icons/Brightness2';
import { vivaDarkTheme, vivaLightTheme } from './theme';

import {
  AlertDisplay,
  OAuthRequestDialog,
  SignInPage,
} from '@backstage/core-components';
import { configApiRef, gitlabAuthApiRef, SignInPageProps, useApi } from '@backstage/core-plugin-api';
import { keycloakAuthApiRef, keycloakEnabled } from './lib/keycloak';
import { createApp } from '@backstage/app-defaults';
import { AppRouter, FlatRoutes } from '@backstage/core-app-api';
import { CatalogGraphPage } from '@backstage/plugin-catalog-graph';
import { RequirePermission } from '@backstage/plugin-permission-react';
import { catalogEntityCreatePermission } from '@backstage/plugin-catalog-common/alpha';
import { NotificationsPage } from '@backstage/plugin-notifications';
import { SignalsDisplay } from '@backstage/plugin-signals';
import { backstagePluginPrometheusPlugin } from '@roadiehq/backstage-plugin-prometheus';
import { translationResources } from './translations';

const app = createApp({
  apis,
  // Русский интерфейс плагинов Backstage (src/translations); en остаётся запасным
  __experimentalTranslations: {
    defaultLanguage: 'ru',
    availableLanguages: ['ru', 'en'],
    resources: translationResources,
  },
  // Плагин Prometheus (Roadie) используется своими компонентами PrometheusGraph /
  // PrometheusAlertStatus (components/metrics), а не routable-расширениями, поэтому его API
  // регистрируется явно
  plugins: [backstagePluginPrometheusPlugin],
  // Темы платформы (src/theme): светлая «Глубокий индиго», тёмная «Графит и мята»
  themes: [
    {
      id: 'light',
      title: 'Светлая',
      variant: 'light',
      icon: <LightIcon />,
      Provider: ({ children }) => (
        <UnifiedThemeProvider theme={vivaLightTheme} children={children} />
      ),
    },
    {
      id: 'dark',
      title: 'Тёмная',
      variant: 'dark',
      icon: <DarkIcon />,
      Provider: ({ children }) => (
        <UnifiedThemeProvider theme={vivaDarkTheme} children={children} />
      ),
    },
  ],
  // Иконки для ссылок каталога (metadata.links[].icon): kubernetes и ml-model — свои,
  // cloud и lock использует шаблон k8s-cluster для ArgoCD и Vault
  icons: {
    kubernetes: KubernetesIcon,
    'ml-model': MlModelIcon,
    cloud: CloudIcon,
    lock: LockIcon,
  },
  bindRoutes({ bind }) {
    bind(catalogPlugin.externalRoutes, {
      createComponent: scaffolderPlugin.routes.root,
      viewTechDoc: techdocsPlugin.routes.docRoot,
      createFromTemplate: scaffolderPlugin.routes.selectedTemplate,
    });
    bind(apiDocsPlugin.externalRoutes, {
      registerApi: catalogImportPlugin.routes.importPage,
    });
    bind(scaffolderPlugin.externalRoutes, {
      registerComponent: catalogImportPlugin.routes.importPage,
      viewTechDoc: techdocsPlugin.routes.docRoot,
    });
    bind(orgPlugin.externalRoutes, {
      catalogIndex: catalogPlugin.routes.catalogIndex,
    });
  },
  components: {
    SignInPage: props => <VivaSignInPage {...props} />,
  },
});

// Вход: Keycloak установки (если подключён — viva.keycloak.url) и GitLab платформы на переходный период;
// гостевого входа нет (провайдер guest в бэкенде не подключён)
function VivaSignInPage(props: SignInPageProps) {
  const config = useApi(configApiRef);
  return (
    <SignInPage
      {...props}
      providers={[
        ...(keycloakEnabled(config)
          ? [{
              id: 'keycloak-auth-provider',
              title: 'Keycloak',
              message: 'Войти учётной записью платформы (Keycloak)',
              apiRef: keycloakAuthApiRef,
            }]
          : []),
        {
          id: 'gitlab-auth-provider',
          title: 'GitLab',
          message: 'Войти через GitLab платформы',
          apiRef: gitlabAuthApiRef,
        },
      ]}
    />
  );
}

const routes = (
  <FlatRoutes>
    <Route path="/" element={<HomePage />} />
    <Route path="/clusters" element={<ClustersPage />} />
    <Route path="/models" element={<ModelsPage />} />
    <Route path="/services" element={<ServicesPage />} />
    <Route path="/jupyterhub" element={<JupyterHubPage />} />
    {/* площадки Proxmox VE: вкладки /proxmox/sites, vms, networks, pools, images */}
    <Route path="/proxmox" element={<ProxmoxPage />} />
    <Route path="/feast" element={<FeastPage />} />
    <Route path="/airflow" element={<AirflowPage />} />
    <Route path="/defectdojo" element={<DefectDojoPage />} />
    <Route path="/databases" element={<DatabasesPage />} />
    <Route path="/brokers" element={<BrokersPage />} />
    <Route path="/mongodb" element={<MongodbPage />} />
    <Route path="/rabbitmq" element={<RabbitmqPage />} />
    <Route path="/valkey" element={<ValkeyPage />} />
    {/* разделы cloud.ru и VK Cloud: страница на managed-сервис; прежний общий адрес — на первый сервис cloud.ru */}
    {MANAGED_SERVICES.map(s => (
      <Route
        key={s.path}
        path={s.path}
        element={<ManagedServicePage service={s} />}
      />
    ))}
    <Route
      path="/managed-datastores"
      element={<Navigate to={vivaRoutes.cloudru} replace />}
    />
    <Route path="/rustfs" element={<RustfsPage />} />
    <Route path="/catalog" element={<CatalogIndexPage />} />
    <Route
      path="/catalog/:namespace/:kind/:name"
      element={<CatalogEntityPage />}
    >
      {entityPage}
    </Route>
    <Route path="/docs" element={<TechDocsIndexPage />} />
    <Route
      path="/docs/:namespace/:kind/:name/*"
      element={<TechDocsReaderPage />}
    >
      <TechDocsAddons>
        <ReportIssue />
      </TechDocsAddons>
    </Route>
    {/* карточки шаблонов с цветом и знаком типа (components/scaffolder) */}
    <Route
      path="/create"
      element={
        <ScaffolderPage
          components={{ TemplateCardComponent: VivaTemplateCard }}
          // шаблоны изменения (тег edit) открываются только кнопкой «Изменить» на странице ресурса;
          // тег hidden — скрытые на этом стенде (cloud.ru и VK Cloud без учётных данных платформы, docker/entrypoint.sh)
          templateFilter={template =>
            !isEditTemplate(template) && !isHiddenTemplate(template)
          }
          headerOptions={{
            pageTitleOverride: 'Создать',
            title: 'Создать',
            subtitle: 'Шаблоны платформы: кластеры, модели, сервисы, хранилища',
          }}
        />
      }
    />
    {/* кнопка «Изменить» ресурса: текущие параметры -> шаблон <тип>-edit */}
    <Route path="/resource-edit" element={<ResourceEditPage />} />
    {/* ИИ-агенты (MCP): подключение и согласие на вход клиента (OAuth CIMD/DCR бэкенда auth) */}
    <Route path="/ai-access" element={<AiAccessPage />} />
    <Route path="/oauth2/authorize/:sessionId" element={<OAuthConsentPage />} />
    <Route path="/api-docs" element={<ApiExplorerPage />} />
    <Route
      path="/catalog-import"
      element={
        <RequirePermission permission={catalogEntityCreatePermission}>
          <CatalogImportPage />
        </RequirePermission>
      }
    />
    <Route path="/search" element={<SearchPage />}>
      {searchPage}
    </Route>
    <Route path="/settings" element={<UserSettingsPage />} />
    <Route path="/catalog-graph" element={<CatalogGraphPage />} />
    <Route path="/notifications" element={<NotificationsPage />} />
  </FlatRoutes>
);

export default app.createRoot(
  <>
    <AlertDisplay />
    <OAuthRequestDialog />
    <SignalsDisplay />
    <AppRouter>
      {/* история посещений для карточки «Недавно открытые» на главной */}
      <VisitListener />
      <Root>{routes}</Root>
    </AppRouter>
  </>,
);
